import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

/* KI-Planer (13.10.2026): Die KI schlägt einen 12-Wochen-Fitness-Zyklus aus EIGENEN Übungen vor und legt ihn als
   ENTWURF an — nichts davon sieht ein Mitglied, bis ein Coach prüft und im Planer freigibt. An das Modell gehen nur die
   Angaben unten, keine Personendaten. Schalter KI_PROGRAMME in Coolify. Aufrufe als Props (CRM und /coach).
   17.10.2026: „Plan für“ einen Kurs (Kursprofile, z. B. alle Frauenkurse) — Tage und Länge kommen vom Kurs, der Entwurf
   hängt am Kurs; „Alle Frauenkurse“ legt je Kurs einen eigenen Entwurf an. */

const fehlerText = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const naechsterMontag = () => {
  const d = new Date(); const t = (8 - d.getDay()) % 7 || 7; d.setDate(d.getDate() + t);
  return d.toLocaleDateString('sv-SE');
};

export default function KiPlaner({ statusLaden, bibliothekLaden, vorschlagen, onAngelegt, testId = 'ki-planer' }) {
  const [s, setS] = useState(null);
  const [uebungen, setUebungen] = useState([]);
  const [auf, setAuf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fuer, setFuer] = useState('');
  const [f, setF] = useState({ ziel: 'fitness', niveau: 'einsteiger', tage: 3, minuten: 60, geraete: [], meiden: [], startdatum: naechsterMontag(), name: '' });
  useEffect(() => {
    if (!auf) return;
    statusLaden().then((r) => { setS(r.data); setF((x) => ({ ...x, geraete: x.geraete.length ? x.geraete : r.data.geraete })); }).catch(() => setS(null));
    bibliothekLaden().then((r) => setUebungen([...r.data.eintraege.map((e) => e.name), ...r.data.offen])).catch(() => setUebungen([]));
  }, [auf, statusLaden, bibliothekLaden]);
  if (s && s.darf === false) return null;
  const geraet = (g) => setF({ ...f, geraete: f.geraete.includes(g) ? f.geraete.filter((x) => x !== g) : [...f.geraete, g] });
  const profile = s?.kursprofile || [];
  const profil = profile.find((p) => p.key === fuer);
  const striking = profil?.vorlage === 'striking';
  const los = async () => {
    setBusy(true);
    const ziele = fuer === '__frauen' ? profile.filter((p) => p.frauen).map((p) => p.key) : (fuer ? [fuer] : [null]);
    for (const k of ziele) {
      const label = profile.find((p) => p.key === k)?.label;
      try {
        const body = k
          ? { kursprofil: k, startdatum: f.startdatum, geraete: f.geraete, meiden: f.meiden, name: ziele.length === 1 ? (f.name || undefined) : undefined }
          : { ...f, tage: Number(f.tage), minuten: Number(f.minuten), name: f.name || undefined };
        const r = await vorschlagen(body);
        toast.success(`Entwurf angelegt: ${r.data.name} – bitte prüfen und freigeben`); onAngelegt?.(r.data);
      } catch (e) { toast.error(`${label ? `${label}: ` : ''}${fehlerText(e, 'Kein Vorschlag')}`); }
    }
    setBusy(false);
  };
  const feld = 'h-10 rounded-md border border-input bg-background px-2 text-sm';
  return (
    <Card className="border-border/60 print:hidden" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-primary" /> KI-Vorschlag für einen Plan</CardTitle>
          <p className="text-xs text-muted-foreground">Nur aus euren Übungen, als Entwurf. Ein Coach prüft und gibt frei – vorher sieht ihn niemand.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAuf((v) => !v)} aria-expanded={auf}>{auf ? 'Zuklappen' : 'Öffnen'}</Button>
      </CardHeader>
      {auf && s && (
        <CardContent className="space-y-3">
          {!s.verfuegbar && <p className="text-sm text-destructive">{s.grund}</p>}
          <label className="block text-xs">Plan für
            <select className={`${feld} w-full`} value={fuer} onChange={(e) => setFuer(e.target.value)} data-testid="ki-plan-fuer">
              <option value="">Freier Plan (Person oder eigene Angaben)</option>
              {profile.map((p) => <option key={p.key} value={p.key}>Kurs: {p.label}</option>)}
              {profile.some((p) => p.frauen) && <option value="__frauen">Alle Frauenkurse (je Kurs ein Entwurf)</option>}
            </select>
          </label>
          {fuer && <p className="text-xs text-muted-foreground">Kurstage und Stundenlänge kommen aus dem Kursplan, der Entwurf hängt am Kurs und ist nach der Freigabe für alle Coaches sichtbar. Zubringer, Power 2 und Abschluss wechseln je Phase (alle 4 Wochen).{(striking || fuer === '__frauen') && ' Ladies Boxing: Phase 1 und Entlastungswochen ohne Sparring, danach nur leichtes Aufgabensparring.'}</p>}
          <div className="grid gap-2 sm:grid-cols-2">
            {!fuer && <>
            <label className="text-xs">Ziel<select className={`${feld} w-full`} value={f.ziel} onChange={(e) => setF({ ...f, ziel: e.target.value })}>{Object.entries(s.ziele).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
            <label className="text-xs">Niveau<select className={`${feld} w-full`} value={f.niveau} onChange={(e) => setF({ ...f, niveau: e.target.value })}>{Object.entries(s.niveaus).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
            <label className="text-xs">Einheiten pro Woche<Input type="number" min={2} max={5} value={f.tage} onChange={(e) => setF({ ...f, tage: e.target.value })} /></label>
            <label className="text-xs">Minuten je Einheit<Input type="number" min={30} max={90} step={5} value={f.minuten} onChange={(e) => setF({ ...f, minuten: e.target.value })} /></label>
            </>}
            <label className="text-xs">Start (Montag)<Input type="date" value={f.startdatum} onChange={(e) => setF({ ...f, startdatum: e.target.value })} /></label>
            {fuer !== '__frauen' && <label className="text-xs">Name (optional, ohne Personennamen)<Input maxLength={120} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>}
          </div>
          {!striking && <>
          <fieldset className="space-y-1"><legend className="text-xs">Ausstattung</legend>
            <div className="flex flex-wrap gap-1.5">{s.geraete.map((g) => (
              <button key={g} type="button" aria-pressed={f.geraete.includes(g)} onClick={() => geraet(g)}
                className={`rounded-full border px-2.5 py-1 text-xs ${f.geraete.includes(g) ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground'}`}>{g}</button>
            ))}</div>
          </fieldset>
          <label className="block text-xs">Übungen meiden (z. B. wegen Beschwerden – nur Auswahl, kein Freitext)
            <select multiple className="mt-1 h-28 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.meiden}
              onChange={(e) => setF({ ...f, meiden: Array.from(e.target.selectedOptions).map((o) => o.value) })}>
              {uebungen.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          </>}
          <Button disabled={busy || !s.verfuegbar} onClick={los}>{busy ? 'Die KI plant … (bis zu einer Minute je Entwurf)' : (fuer === '__frauen' ? 'Entwürfe für alle Frauenkurse erstellen' : 'Entwurf erstellen')}</Button>
        </CardContent>
      )}
    </Card>
  );
}

const crmStatus = () => api.get('/trainingsplaner/ki-status');
const crmBib = () => api.get('/trainingsplaner/bibliothek');
const crmVorschlag = (b) => api.post('/trainingsplaner/ki-vorschlag', b);

export function KiPlanerCrm({ onAngelegt }) {
  return <KiPlaner statusLaden={crmStatus} bibliothekLaden={crmBib} vorschlagen={crmVorschlag} onAngelegt={onAngelegt} />;
}
